// ============================================
// P2 E-02：AI 助手 API
// - status：开关/模型/配额
// - sessions：会话列表/消息/删除
// - chatStream：SSE 流式（fetch ReadableStream 解析），失败可由调用方降级非流式
// ============================================
import client from './client';
import useAuthStore from '../stores/authStore';
import { extractSseEvents } from '../utils/aiSse';

export interface AiStatus {
  enabled: boolean;
  protocol: string;
  model: string;
  models: string[];
  mock: boolean;
  quota: { packageCode: string; limit: number; used: number; remaining: number };
}

export interface AiRefItem {
  slug: string;
  title: string;
  url: string;
  summary?: string;
}

export interface AiSessionItem {
  id: string;
  title: string;
  updatedAt: string;
  messageCount: number;
}

export interface AiMessageItem {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  refs?: AiRefItem[];
  model?: string;
  createdAt: string;
}

export async function fetchAiStatus(): Promise<AiStatus> {
  const { data } = await client.get('/ai/status');
  return data;
}

export async function fetchAiSessions(): Promise<{ items: AiSessionItem[] }> {
  const { data } = await client.get('/ai/sessions');
  return data;
}

export async function fetchAiMessages(sessionId: string): Promise<{ session: { id: string; title: string }; items: AiMessageItem[] }> {
  const { data } = await client.get(`/ai/sessions/${sessionId}/messages`);
  return data;
}

export async function deleteAiSession(sessionId: string): Promise<{ success: boolean }> {
  const { data } = await client.delete(`/ai/sessions/${sessionId}`);
  return data;
}

export async function chatAiOnce(body: { message: string; sessionId?: string; model?: string }) {
  const { data } = await client.post('/ai/chat', { ...body, stream: false });
  return data as { sessionId: string; messageId: string; content: string; refs: AiRefItem[]; model: string };
}

/** 2026-10-26 Phase B：需求/样例 → 设备解析 JS（仅可视化表达不了的场景） */
export async function generateParseScript(body: {
  requirements?: string;
  sample?: unknown;
  samples?: unknown[];
  targetFields?: string[];
  protocol?: string;
  autoRepair?: boolean;
  maxAttempts?: number;
  model?: string;
}) {
  const { data } = await client.post('/ai/parse-script', body, { timeout: 120000 });
  return data as {
    ok: boolean;
    script?: string;
    explanation?: string;
    errors?: string[];
    attempts?: number;
    testResults?: Array<{ name: string; ok: boolean; pass?: boolean; result?: unknown; error?: string; ms?: number }>;
    model?: string;
    raw?: string;
  };
}
export async function generateParseSpec(body: {
  sample?: unknown;
  samples?: unknown[];
  mode: 'rows' | 'object';
  requirements?: string;
  notes?: string;
  targetFields?: string[];
  protocol?: string;
  constraints?: Record<string, unknown>;
  autoRepair?: boolean;
  maxAttempts?: number;
}) {
  const { data } = await client.post('/ai/parse-spec', body, { timeout: 120000 });
  return data as {
    ok: boolean;
    spec?: unknown;
    errors?: string[];
    explanation?: string;
    warnings?: string[];
    attempts?: number;
    testResults?: Array<{ name: string; ok: boolean; pass?: boolean; result?: unknown; error?: string; ms?: number }>;
    model?: string;
    raw?: string;
    templates?: string[];
  };
}

export type AiStreamEvent =
  | { type: 'meta'; sessionId: string; model: string; refs: AiRefItem[] }
  | { type: 'delta'; text: string }
  | { type: 'done'; messageId: string }
  | { type: 'error'; code?: number; message: string };

/**
 * SSE 流式对话：POST + ReadableStream 逐块解析。
 * 使用原生 fetch（axios 不支持流式读取）；鉴权头手动携带。
 * onEvent 逐个事件回调；异常抛出由调用方处理（可降级 chatAiOnce）。
 */
export async function chatAiStream(
  body: { message: string; sessionId?: string; model?: string },
  onEvent: (ev: AiStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = useAuthStore.getState().token || '';
  const res = await fetch('/api/v1/ai/chat', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ ...body, stream: true }),
    signal,
  });
  if (!res.ok || !res.body) {
    let message = `请求失败（${res.status}）`;
    let code: number | undefined = res.status;
    try {
      const j = await res.json();
      message = j?.message || j?.msg || message;
      code = j?.statusCode || j?.code || code;
    } catch { /* ignore */ }
    throw Object.assign(new Error(String(message)), { code });
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const { blocks, rest } = extractSseEvents(buf);
    buf = rest;
    for (const { event: evName, data: dataLine } of blocks) {
      try {
        const payload = JSON.parse(dataLine);
        if (evName === 'meta') onEvent({ type: 'meta', sessionId: payload.sessionId, model: payload.model, refs: payload.refs || [] });
        else if (evName === 'delta') onEvent({ type: 'delta', text: payload.text || '' });
        else if (evName === 'done') onEvent({ type: 'done', messageId: payload.messageId });
        else if (evName === 'error') onEvent({ type: 'error', code: payload.code, message: payload.message || 'AI 对话失败' });
      } catch { /* 忽略坏块 */ }
    }
  }
}
